import { canMeasureGoogle, googleMeasurementConsent, safePage } from './googleMeasurementConsent.js';
export { safePage } from './googleMeasurementConsent.js';
import { getLeadTracking } from './leadTracking.js';

export const MEASUREMENT_ID = 'G-NK123DSV05';
const ORIGIN = 'https://trinitydentalarts.com';
const EVENTS = new Set(['page_view', 'phone_click', 'consultation_booking_start', 'consultation_request_submitted']);
let started = false;
let lastPath = '';
let previousPage = '';
let gtmScheduled = false;

function injectGtm() {
  // Own the loader as well as the events; the site must not depend on an
  // undocumented edge injection. Reuse any already-present Google loader.
  //
  // The Google tag (gtag.js) is loaded directly rather than through the
  // GTM-TWVN5W5Z container. Audit of the live container (Nov 3, 2026): its
  // only active tag was the Google tag for G-NK123DSV05, fired on the
  // `tda_analytics_ready` event with exactly the config this file already
  // sends via gtag('config'); the other four tags (a GA4 event, an Ads
  // conversion, a conversion linker and a custom HTML tag) are paused. The
  // container therefore cost ~120 KB and ~110 ms of main-thread time to do
  // what one script tag does. AW-18550821750 is configured by
  // googleAdsConversions.js through the same gtag queue, so gtag.js loads the
  // Ads destination itself; conversions and consent mode are unchanged.
  // To bring the container back, point `src` at gtm.js?id=GTM-TWVN5W5Z again.
  if (document.querySelector?.('script[src*="googletagmanager.com/gtag/js"], script[src*="googletagmanager.com/gtm.js"], script[src*="/zct5/"]')) return;
  const script = document.createElement('script');
  script.id = 'tda-gtag'; script.async = true;
  script.src = 'https://www.googletagmanager.com/gtag/js?id=' + MEASUREMENT_ID;
  script.referrerPolicy = 'no-referrer';
  document.head.appendChild(script);
}

// The Google tag and its Ads destination are ~330 KB and ~0.3 s of
// main-thread work on a mid-range phone. Load them once the page is usable:
// on the first user interaction, or shortly after window load, whichever
// comes first. Every consent, config, page_view and conversion pushed
// meanwhile waits in the dataLayer queue and is processed in order when the
// tag initializes.
function scheduleGtmLoad() {
  if (gtmScheduled || typeof window === 'undefined') return;
  gtmScheduled = true;
  let done = false;
  const events = ['pointerdown', 'keydown', 'touchstart', 'scroll'];
  // Parsing and executing the Google tags is a single ~250 ms task. Injecting
  // it inline from the interaction handler put that task immediately after the
  // user's tap, delaying the response. Let the interaction finish rendering
  // first, then inject when the main thread is free.
  const injectLater = (delay) => {
    setTimeout(() => {
      if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(injectGtm, { timeout: 1500 });
      else injectGtm();
    }, delay);
  };
  const fire = () => {
    if (done) return;
    done = true;
    events.forEach(name => window.removeEventListener?.(name, fire));
    injectLater(500);
  };
  events.forEach(name => window.addEventListener?.(name, fire, { passive: true }));
  const afterLoad = () => {
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(fire, { timeout: 2500 });
    else setTimeout(fire, 1500);
  };
  if (document.readyState === 'complete') afterLoad();
  else window.addEventListener?.('load', afterLoad, { once: true });
}

export function safeReferrer(value) {
  try {
    const url = new URL(value);
    if (!['https:', 'http:'].includes(url.protocol)) return '';
    if (url.origin === ORIGIN) return safePage(url.href);
    // Keep the referring site for attribution; never transmit its path or query.
    return url.origin + '/';
  } catch { return ''; }
}

export function syncMeasurementConsent() {
  const allowed = canMeasureGoogle();
  window['ga-disable-' + MEASUREMENT_ID] = !allowed;
  window.gtag?.('consent', 'update', googleMeasurementConsent());
  if (!allowed) { lastPath = ''; return false; }
  if (!started) {
    started = true;
    // Register the destination before queuing events. The consent-triggered
    // GTM tag may finish loading after the first pageview is already queued.
    window.gtag?.('js', new Date());
    window.gtag?.('config', MEASUREMENT_ID, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      allow_enhanced_conversions: false,
      page_location: ORIGIN + '/',
      page_referrer: '',
      page_title: 'Trinity Dental Arts',
    });
    scheduleGtmLoad();
    // Kept for the (currently unused) GTM container, whose Google tag fires on
    // this trigger; harmless for the directly loaded gtag.js.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'tda_analytics_ready' });
  }
  return true;
}

export function trackEvent(name, properties = {}, onComplete) {
  try {
    const page = safePage(window.location.pathname + (window.location.search || ''));
    if (!EVENTS.has(name) || !page || !syncMeasurementConsent()) return false;
    const payload = {
      send_to: MEASUREMENT_ID,
      page_location: page,
      page_title: 'Trinity Dental Arts',
      page_referrer: previousPage || safeReferrer(document.referrer),
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    };
    const campaign = getLeadTracking();
    // Preserve only opaque advertising click IDs in the measurement URL; all
    // other query parameters and fragments remain excluded.
    const clickIds = new URLSearchParams();
    for (const key of ['gclid', 'gbraid', 'wbraid']) {
      if (/^[a-z0-9_-]{10,200}$/i.test(campaign[key] || '')) clickIds.set(key, campaign[key]);
    }
    if (clickIds.size) payload.page_location += (payload.page_location.includes('?') ? '&' : '?') + clickIds.toString();
    for (const [source, target] of Object.entries({ utm_source: 'campaign_source', utm_medium: 'campaign_medium', utm_campaign: 'campaign_name', utm_content: 'campaign_content' })) {
      if (campaign[source]) payload[target] = campaign[source];
    }
    if (typeof onComplete === 'function') { payload.event_callback = onComplete; payload.event_timeout = 800; }
    payload.transport_type = 'beacon';
    window.gtag?.('event', name, payload);
    return true;
  } catch { return false; }
}

export function trackPage() {
  const path = safePage(window.location.pathname + (window.location.search || ''));
  if (!path || !syncMeasurementConsent() || lastPath === path) return false;
  if (!trackEvent('page_view')) return false;
  lastPath = path;
  previousPage = path;
  return true;
}